// Badge colorimétrique d'échéance d'une facture (Lot 3A).
//
// Règles (basées sur due_date vs today) :
//   vert   : pas échue, > 7 jours restants
//   ambre  : ≤ 7 jours avant OU retard ≤ 30 jours
//   rouge  : retard > 30 jours OU statut OVERDUE effectif
//   neutre : statut PAID / CANCELLED / DRAFT (pas pertinent)
//
// On prend en entrée le `effective_status` calculé côté serveur + la due_date
// pour afficher un libellé humain ("dans 3 j" / "retard 12 j" / "soldée").

import type { InvoiceStatus } from "../api/types";

type Props = {
  dueDate: string; // ISO YYYY-MM-DD
  effectiveStatus: InvoiceStatus;
  today?: Date; // injectable pour les tests
};

export type DueBucket = "green" | "amber" | "red" | "neutral";

function diffDays(a: Date, b: Date): number {
  const MS = 1000 * 60 * 60 * 24;
  // On normalise à minuit UTC pour que "aujourd'hui" soit stable
  const da = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const db = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((da - db) / MS);
}

export function dueBucket(
  dueDate: string,
  effectiveStatus: InvoiceStatus,
  today: Date = new Date(),
): DueBucket {
  if (
    effectiveStatus === "paid" ||
    effectiveStatus === "cancelled" ||
    effectiveStatus === "draft"
  ) {
    return "neutral";
  }
  const due = new Date(dueDate + "T00:00:00Z");
  const days = diffDays(due, today); // jours jusqu'à échéance (négatif si retard)
  if (days < -30) return "red";
  if (days < 7) return "amber";
  return "green";
}

const BUCKET_CLASSES: Record<DueBucket, string> = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  red: "bg-red-50 text-red-700 border-red-200",
  neutral: "bg-slate-50 text-slate-500 border-slate-200",
};

function humanLabel(
  dueDate: string,
  effectiveStatus: InvoiceStatus,
  today: Date,
): string {
  if (effectiveStatus === "paid") return "Soldée";
  if (effectiveStatus === "cancelled") return "Annulée";
  if (effectiveStatus === "draft") return "Brouillon";
  const due = new Date(dueDate + "T00:00:00Z");
  const days = diffDays(due, today);
  if (days === 0) return "Échéance aujourd'hui";
  if (days > 0) {
    if (days === 1) return "Dans 1 jour";
    return `Dans ${days} jours`;
  }
  const lateDays = -days;
  if (lateDays === 1) return "Retard 1 jour";
  return `Retard ${lateDays} jours`;
}

export function DueBadge({ dueDate, effectiveStatus, today = new Date() }: Props) {
  const bucket = dueBucket(dueDate, effectiveStatus, today);
  const label = humanLabel(dueDate, effectiveStatus, today);
  return (
    <span
      role="status"
      className={[
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
        BUCKET_CLASSES[bucket],
      ].join(" ")}
      data-bucket={bucket}
      aria-label={label}
      title={label}
    >
      <span aria-hidden="true">●</span>
      <span>{label}</span>
    </span>
  );
}
